
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ThumbsUp, MapPin, Clock, Share2, Star, ImageIcon } from 'lucide-react';
import { getCategories } from '@/utils/categories';
import { ShareButtons } from './social/ShareButtons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { motion } from 'framer-motion';
import { fetchReportPhotos } from '@/utils/reportPhotos';

interface ReportCardProps {
  id: number;
  title: string;
  location: string;
  date: string;
  category: string;
  supports: number;
  onSupportClick?: () => void;
  onClick?: () => void;
}

const ReportCard = ({ 
  id, title, location, date, category, supports, 
  onSupportClick, onClick
}: ReportCardProps) => {
  // Find category data from getCategories()
  const allCategories = getCategories();
  const categoryData = allCategories.find(cat => cat.id === category) || { 
    id: category,
    label: category, 
    icon: MapPin,
    description: '',
    imageUrl: '',
  };
  
  // Use local storage to track favorites
  const [favorites, setFavorites] = useLocalStorage<number[]>('user_favorites', []);
  const isFavorite = favorites.includes(id);
  
  const getCategoryColorClass = (categoryId: string) => {
    const colorMap: Record<string, string> = {
      'voirie': 'bg-amber-600',
      'proprete': 'bg-green-600',
      'eclairage': 'bg-yellow-500',
      'transport': 'bg-blue-600',
      'espaces-verts': 'bg-emerald-600',
      'eau': 'bg-sky-600',
      'securite': 'bg-red-600',
      'pollution-air': 'bg-indigo-600',
      'pollution-eau': 'bg-cyan-600',
      'nuisance-sonore': 'bg-purple-600',
      'biodiversite': 'bg-lime-600',
      'flore-invasive': 'bg-green-800',
      'inondation': 'bg-blue-800',
      'ilot-chaleur': 'bg-orange-600',
      'erosion': 'bg-amber-800',
      'ventilation': 'bg-slate-600',
      'pollution-visuelle': 'bg-fuchsia-600',
      'commerce-alimentaire': 'bg-rose-600',
      'commerce': 'bg-pink-600',
      'urgence': 'bg-red-800',
      'terrain-abandonne': 'bg-stone-600',
    };
    
    return colorMap[categoryId] || 'bg-gray-500';
  };
  
  const formattedDate = new Date(date).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  useEffect(() => {
    fetchReportPhotos(id).then((urls) => {
      if (urls.length > 0) setThumbnail(urls[0]);
    }).catch(() => {});
  }, [id]);

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowShareOptions(!showShareOptions);
  };
  
  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFavorite) {
      setFavorites(favorites.filter(favId => favId !== id));
    } else {
      setFavorites([...favorites, id]);
    }
  };

  return (
    <motion.div 
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      <Card 
        className={`hover:shadow-lg transition-shadow cursor-pointer border dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 ${isHovered ? 'ring-2 ring-primary ring-opacity-50' : ''}`} 
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <Badge className={`${getCategoryColorClass(categoryData.id)} text-white`}>
              {categoryData.label}
            </Badge>
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-3 h-3 mr-1" />
              {formattedDate}
            </div>
          </div>
          <CardTitle className="text-lg mt-2 line-clamp-2 dark:text-white">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          {thumbnail && (
            <img
              src={thumbnail}
              alt={`Photo de ${title}`}
              className="rounded-md object-cover aspect-video w-full mb-3"
              loading="lazy"
            />
          )}
          <div className="flex items-start text-gray-600 dark:text-gray-400 mb-4">
            <MapPin className="w-4 h-4 mr-2 shrink-0 mt-0.5" />
            <span className="text-sm line-clamp-2">{location}</span>
          </div>
        </CardContent>
        <CardFooter className="pt-0 flex justify-between">
          <div className="flex items-center">
            <Avatar className="h-6 w-6 mr-2">
              <AvatarFallback className="text-xs bg-primary text-white">U</AvatarFallback>
            </Avatar>
            <span className="text-xs text-gray-500 dark:text-gray-400">Citoyen</span>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="ghost" 
              className={`px-2 ${isFavorite ? 'text-yellow-500' : 'text-gray-400 dark:text-gray-500'} hover:text-yellow-500 transition-colors`}
              onClick={toggleFavorite}
              title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
            >
              <Star className="h-4 w-4" fill={isFavorite ? "currentColor" : "none"} />
            </Button>
            
            <Popover>
              <PopoverTrigger asChild onClick={handleShareClick}>
                <Button size="sm" variant="ghost" className="px-2 text-gray-400 dark:text-gray-500 hover:text-primary transition-colors">
                  <Share2 className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2 bg-white dark:bg-gray-900 border dark:border-gray-800" onClick={(e) => e.stopPropagation()}>
                <ShareButtons
                  title={`Signalement: ${title}`}
                  description={`Un signalement à ${location}`}
                  url={`${window.location.origin}/report/${id}`}
                  showLabel={false}
                  size="sm"
                  variant="ghost"
                />
              </PopoverContent>
            </Popover>

            <Button 
              size="sm" 
              variant="outline" 
              className="flex items-center gap-1 hover:bg-primary/10 hover:text-primary transition-colors dark:hover:bg-primary/20"
              onClick={(e) => {
                e.stopPropagation();
                onSupportClick?.();
              }}
            >
              <ThumbsUp className="h-3.5 w-3.5" />
              <span>{supports}</span>
            </Button>
          </div>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default ReportCard;
